import { CardContent, Separator } from '@ais-ui/ui-core';
import { VirtualAtonTargetInfo } from '@ais-ui/websockets';
import React from 'react';
import { useSettings } from '../../Settings/SettingsProvider';
import { useLengthUnits } from '../../Units/useLengthUnits';

interface VirtualAtonInfoTooltipProps {
  virtualAtonInfo: VirtualAtonTargetInfo;
}

interface VirtualAtonInfoTooltipItemProps {
  label: string;
  value: string;
}

const VirtualAtonInfoTooltipItem = ({
  label,
  value,
}: VirtualAtonInfoTooltipItemProps) => {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2 max-w-[12rem]">
      <p className="text-xs font-medium flex-none text-left max-w-[6rem]">
        {label}
      </p>
      <p className="text-xs text-muted-foreground flex-grow text-right">
        {value ? value : 'Unknown'}
      </p>
    </div>
  );
};

export const VirtualAtonInfoTooltip = ({
  virtualAtonInfo,
}: VirtualAtonInfoTooltipProps) => {
  const { settings } = useSettings();
  const ribbonSettings = settings['ribbonPlugin.settings'];
  const { convertLengthString } = useLengthUnits();

  // Check if any of the settings are enabled and corresponding data exists
  const hasVisibleItems =
    (ribbonSettings?.show_virtual_aton_assigned_mode &&
      virtualAtonInfo.assignedMode) ||
    (ribbonSettings?.show_virtual_aton_type && virtualAtonInfo.atonType) ||
    (ribbonSettings?.show_virtual_aton_target_type &&
      virtualAtonInfo.targetType) ||
    (ribbonSettings?.show_virtual_aton_country && virtualAtonInfo.country) ||
    (ribbonSettings?.show_virtual_aton_latitude && virtualAtonInfo.latitude) ||
    (ribbonSettings?.show_virtual_aton_longitude &&
      virtualAtonInfo.longitude) ||
    (ribbonSettings?.show_virtual_aton_length && virtualAtonInfo.length) ||
    (ribbonSettings?.show_virtual_aton_mmsi && virtualAtonInfo.mmsi) ||
    (ribbonSettings?.show_virtual_aton_name && virtualAtonInfo.name) ||
    (ribbonSettings?.show_virtual_aton_off_position &&
      virtualAtonInfo.offPosition) ||
    (ribbonSettings?.show_virtual_aton_state && virtualAtonInfo.state) ||
    (ribbonSettings?.show_virtual_aton_status && virtualAtonInfo.status) ||
    (ribbonSettings?.show_virtual_aton_width && virtualAtonInfo.width);

  // Return null if no items will be shown
  if (!hasVisibleItems) {
    return null;
  }

  return (
    <CardContent className="pt-0 pb-2 -mt-1 space-y-2 px-3">
      <Separator className="-ml-1"></Separator>
      <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
        {ribbonSettings?.show_virtual_aton_assigned_mode &&
          virtualAtonInfo.assignedMode && (
            <VirtualAtonInfoTooltipItem
              key="assignedMode"
              label="Assigned mode"
              value={virtualAtonInfo.assignedMode}
            ></VirtualAtonInfoTooltipItem>
          )}
        {ribbonSettings?.show_virtual_aton_type && virtualAtonInfo.atonType && (
          <VirtualAtonInfoTooltipItem
            key="atonType"
            label="Virtual AtoN type"
            value={virtualAtonInfo.atonType}
          ></VirtualAtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_virtual_aton_target_type &&
          virtualAtonInfo.targetType && (
            <VirtualAtonInfoTooltipItem
              key="virtualAtonTargetType"
              label="Target type"
              value={virtualAtonInfo.targetType}
            ></VirtualAtonInfoTooltipItem>
          )}
        {ribbonSettings?.show_virtual_aton_country &&
          virtualAtonInfo.country && (
            <VirtualAtonInfoTooltipItem
              key="country"
              label="Country"
              value={virtualAtonInfo.country}
            ></VirtualAtonInfoTooltipItem>
          )}
        {ribbonSettings?.show_virtual_aton_latitude &&
          virtualAtonInfo.latitude && (
            <VirtualAtonInfoTooltipItem
              key="latitude"
              label="Latitude"
              value={virtualAtonInfo.latitude.toString()}
            ></VirtualAtonInfoTooltipItem>
          )}
        {ribbonSettings?.show_virtual_aton_longitude &&
          virtualAtonInfo.longitude && (
            <VirtualAtonInfoTooltipItem
              key="longitude"
              label="Longitude"
              value={virtualAtonInfo.longitude.toString()}
            ></VirtualAtonInfoTooltipItem>
          )}
        {ribbonSettings?.show_virtual_aton_length && virtualAtonInfo.length && (
          <VirtualAtonInfoTooltipItem
            key="length"
            label="Length"
            value={convertLengthString(virtualAtonInfo.length)}
          ></VirtualAtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_virtual_aton_mmsi && virtualAtonInfo.mmsi && (
          <VirtualAtonInfoTooltipItem
            key="mmsi"
            label="MMSI"
            value={virtualAtonInfo.mmsi}
          ></VirtualAtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_virtual_aton_name && virtualAtonInfo.name && (
          <VirtualAtonInfoTooltipItem
            key="name"
            label="Name"
            value={virtualAtonInfo.name}
          ></VirtualAtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_virtual_aton_off_position &&
          virtualAtonInfo.offPosition && (
            <VirtualAtonInfoTooltipItem
              key="offPosition"
              label="Off position"
              value={virtualAtonInfo.offPosition}
            ></VirtualAtonInfoTooltipItem>
          )}
        {ribbonSettings?.show_virtual_aton_state && virtualAtonInfo.state && (
          <VirtualAtonInfoTooltipItem
            key="state"
            label="State"
            value={virtualAtonInfo.state}
          ></VirtualAtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_virtual_aton_status && virtualAtonInfo.status && (
          <VirtualAtonInfoTooltipItem
            key="status"
            label="Status"
            value={virtualAtonInfo.status}
          ></VirtualAtonInfoTooltipItem>
        )}
        {ribbonSettings?.show_virtual_aton_width && virtualAtonInfo.width && (
          <VirtualAtonInfoTooltipItem
            key="width"
            label="Width"
            value={convertLengthString(virtualAtonInfo.width)}
          ></VirtualAtonInfoTooltipItem>
        )}
      </div>
    </CardContent>
  );
};
